/**
 * Server-state mutation hooks for agent CRUD.
 *
 * Each hook is a thin useMutation that:
 *   1. Calls the matching agentService method.
 *   2. Invalidates the ['agents'] query on success so useAgentsQuery
 *      re-fetches the canonical list.
 *
 * Why one file: every hook is ~6 lines and they share identical
 * invalidation boilerplate. Eight files would be churn for no readability
 * win.
 *
 * useSetDefaultAgent preserves the Promise.all parallelism introduced by
 * the F-17-partial plan — sibling-clear PUTs are issued in parallel,
 * sequenced before the new-default set.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { agentService } from '@/services/agentService';
import type { Agent, AgentInstallRequest } from '@/types/agent.types';
import type { ExportEnvelope } from '@/services/agentExportImport/schema';
import { AGENTS_QUERY_KEY } from './useAgentsQuery';

function useInvalidateAgents() {
  const queryClient = useQueryClient();
  // refetchType: 'none' marks the query stale without firing an
  // immediate refetch. The AgentContext wrapper applies an optimistic
  // cache patch synchronously after each mutation; an automatic
  // refetch here would race the patch and undo it. The next mount /
  // window-focus / explicit refresh picks up the canonical list.
  return () =>
    queryClient.invalidateQueries({
      queryKey: [...AGENTS_QUERY_KEY],
      refetchType: 'none',
    });
}

export function useInstallAgent(): UseMutationResult<
  Agent,
  Error,
  AgentInstallRequest
> {
  const invalidate = useInvalidateAgents();
  return useMutation<Agent, Error, AgentInstallRequest>({
    mutationFn: (request) => agentService.installAgent(request),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export interface ImportAgentArgs {
  payload: ExportEnvelope;
  mode: 'create' | 'update';
  targetAgentId?: string;
  secretOverrides?: Record<string, string>;
}

export function useImportAgent(): UseMutationResult<
  Agent,
  Error,
  ImportAgentArgs
> {
  const invalidate = useInvalidateAgents();
  return useMutation<Agent, Error, ImportAgentArgs>({
    mutationFn: (args) => agentService.importAgent(args),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useUpdateAgent(): UseMutationResult<
  void,
  Error,
  { id: string; updates: Partial<Agent> }
> {
  const invalidate = useInvalidateAgents();
  return useMutation<void, Error, { id: string; updates: Partial<Agent> }>({
    mutationFn: ({ id, updates }) => agentService.updateAgent(id, updates),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useDeleteAgent(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateAgents();
  return useMutation<void, Error, string>({
    mutationFn: (id) => agentService.deleteAgent(id),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useDuplicateAgent(): UseMutationResult<
  Agent,
  Error,
  { id: string; name?: string }
> {
  const invalidate = useInvalidateAgents();
  return useMutation<Agent, Error, { id: string; name?: string }>({
    mutationFn: ({ id, name }) => agentService.duplicateAgent(id, name),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useActivateAgent(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateAgents();
  return useMutation<void, Error, string>({
    mutationFn: (id) => agentService.activateAgent(id),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useDeactivateAgent(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateAgents();
  return useMutation<void, Error, string>({
    mutationFn: (id) => agentService.deactivateAgent(id),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useSetDefaultAgent(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateAgents();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      // Read the current agent list from cache rather than refetching —
      // we just need to know which siblings have isDefault: true today.
      // If the cache is empty (mutation fired before the query settled),
      // fall back to a one-shot getAllAgents call (matches legacy
      // behavior, which always called loadAgents() implicitly).
      let agents = queryClient
        .getQueriesData<Agent[]>({ queryKey: [...AGENTS_QUERY_KEY] })
        .flatMap(([, data]) => data ?? []);
      if (agents.length === 0) {
        agents = await agentService.getAllAgents();
      }

      // Parallelize sibling-clear (preserves F-17 partial behavior). The
      // PUTs are independent (each touches a different row) and order
      // doesn't matter — Promise.all instead of for-await saves N-1
      // round-trips.
      await Promise.all(
        agents
          .filter((a) => a.isDefault && a.id !== id)
          .map((a) => agentService.updateAgent(a.id, { isDefault: false }))
      );
      // Sequence the new-default set after the clears resolve so the DB
      // only briefly observes "no default" rather than "two defaults" if
      // a concurrent read sneaks in.
      await agentService.updateAgent(id, { isDefault: true });
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}
